import React, { useState, useContext } from 'react'
import { useTransition, animated } from 'react-spring'
import styled from 'styled-components'

import colors from '../styles/colors'
import { media } from '../utils/media'

let NotificationsContext

const { Provider, Consumer } = (NotificationsContext = React.createContext(
  undefined
))

const NotificationProvider = ({
  config = { tension: 125, friction: 20, precision: 0.1 },
  timeout = 500,
  children,
}) => {
  const [items, setItems] = useState([])
  const [refMap] = useState(() => new WeakMap())

  const transitions = useTransition(items, (item) => item.id, {
    from: { opacity: 0, height: 0, life: 100 },
    enter: (item) => async (next) =>
      next({
        opacity: 1,
        height: refMap.get(item).offsetHeight,
      }),
    leave: () => async (next) => {
      await next({ opacity: 0 })
      await next({ height: 0 })
    },
    onRest: async (item) => {
      await new Promise((resolve) => setTimeout(resolve, 4000))
      setItems((state) => state.filter((i) => i.id !== item.id))
    },
    config: (_, state) =>
      state === 'leave' ? [{ duration: timeout }, config, config] : config,
  })

  return (
    <Provider
      value={{
        notify: (text, status = 'success') =>
          setItems((state) => [
            ...state,
            {
              id: Math.random(),
              text,
              status,
            },
          ]),
      }}
    >
      <Container>
        {transitions.map(({ key, item, props: { life, ...style } }) => (
          <Message key={key} style={style}>
            <Content status={item.status} ref={(ref) => refMap.set(item, ref)}>
              <P role="alert">{item.text}</P>
              <Button
                onClick={() =>
                  setItems((state) => state.filter((i) => i.id !== item.id))
                }
              >
                X
              </Button>
            </Content>
          </Message>
        ))}
      </Container>
      {children}
    </Provider>
  )
}

export const Container = styled.div`
  position: fixed;
  z-index: 1000;
  width: auto;
  bottom: 30px;
  margin: 0 auto;
  left: 30px;
  display: flex;
  flex-direction: column;
  pointer-events: none;
  align-items: ${(props) =>
    props.position === 'center' ? 'center' : `flex-${props.position || 'end'}`};
`

export const Message = styled(animated.div)`
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  width: 40ch;
  ${media.medium`width: 100%;`}
`

export const Content = styled.div`
  color: white;
  background: ${(props) =>
    props.status === 'success' ? colors.mantis_green : colors.monza};
  opacity: 0.9;
  padding: 12px 22px 24px;
  font-size: 1em;
  display: grid;
  grid-template-columns: ${(props) =>
    props.canClose === false ? '1fr' : '1fr auto'};
  grid-gap: 10px;
  overflow: hidden;
  border-radius: 3px;
  margin-top: ${(props) => (props.top ? '0' : '10px')};
  margin-bottom: ${(props) => (props.top ? '10px' : '0')};
`

export const Button = styled('button')`
  cursor: pointer;
  pointer-events: all;
  outline: 0;
  border: none;
  background: transparent;
  display: flex;
  align-self: flex-end;
  overflow: hidden;
  margin: 0;
  padding: 0;
  padding-bottom: 14px;
  color: rgba(255, 255, 255, 0.5);
  :hover {
    color: rgba(255, 255, 255, 1);
  }
`

const P = styled.p`
  margin: 0;
`

const withNotifier = (Component) => (props) => (
  <Consumer>
    {({ notify }) => <Component notify={notify} {...props} />}
  </Consumer>
)

export const useNotifications = () => {
  const context = useContext(NotificationsContext)
  if (!context)
    throw new Error(
      'use Notifications should be wrapped in Notifications Provider'
    )

  return context
}

export { NotificationProvider, Consumer as Notifier, withNotifier }
export default NotificationsContext
